import { useState, useRef, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import logo from '../../assets/images/logo-back-clear.svg';
import cardPay from '../../assets/images/card-payment.svg';
import { payMembership } from "../../api/PayApi";
import { useAuth } from '../../context/AuthContext';
import './PaymentForm.css';
import LoadingModal from './LoadingModal';
import countriesData from '../../data/countries.json';

const PaymentForm = () => {
  const { refreshMembership, user } = useAuth();
  const validateTwoWords = (value) => {
  const words = value.trim().split(/\s+/); // separa por espacios
  return words.length >= 2;
};

  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: '',
    cardNumber: '',
    expireDate: '',
    cvv: '',
    cardName: '',
    country: '',
    address: '',
    city: '',
  });
  const [modalState, setModalState] = useState({
    isOpen: false,
    status: 'loading',
    message: ''
  });

  // Estados para el buscador de países
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const countryDropdownRef = useRef(null);

  // Filtrar países cuando cambia la búsqueda
  useEffect(() => {
    if (countrySearch.trim() === '') {
      setFilteredCountries(Object.entries(countriesData));
    } else {
      const searchLower = countrySearch.toLowerCase();
      const filtered = Object.entries(countriesData).filter(([code, name]) =>
        name.toLowerCase().includes(searchLower) || code.toLowerCase().includes(searchLower)
      );
      setFilteredCountries(filtered);
    }
  }, [countrySearch]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCountrySelect = (countryCode, countryName) => {
    setForm({ ...form, country: countryCode });
    setCountrySearch(countryName);
    setShowCountryDropdown(false);
  };

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleExpireDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Solo números
    
    if (value.length >= 2) {
      value = value.slice(0, 2) + '/' + value.slice(2, 4);
    }
    
    setForm({
      ...form,
      expireDate: value,
    });
  };

  const validateForm = () => {
    const newErrors = [];

    if (!form.email.trim()) {
      newErrors.push('Email is required');
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.push('The email address is invalid.');
    }

    if (!form.cardNumber.trim()) {
      newErrors.push('The card number is required');
    } else if (!/^\d{13,19}$/.test(form.cardNumber)) {
      newErrors.push('It must be between 13 and 19 digits');
    }

    if (!form.expireDate.trim()) {
      newErrors.push('The expiration date is mandatory');
    } else if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(form.expireDate)) {
      newErrors.push('Invalid format (MM/YY)');
    }

    if (!form.cvv.trim()) {
      newErrors.push('The CVV is mandatory');
    } else if (!/^\d{3,4}$/.test(form.cvv)) {
      newErrors.push('It must have 3 or 4 digits');
    }

    if (!form.cardName.trim()){
      newErrors.push('The name on the card is required');
    } else if (!validateTwoWords(form.cardName)) {
      newErrors.push('The name must contain at least two words');
    }
    if (!form.country.trim()) newErrors.push('the country is mandatory');
    if (!form.address.trim()) newErrors.push('The address is required');
    if (!form.city.trim()) newErrors.push('The city is mandatory');

    const lastError = newErrors.length > 0 ? newErrors[newErrors.length - 1] : null;

    return lastError;
  };

  async function handleSubmit(e) {
    e.preventDefault();

    const lastError = validateForm();
    if (lastError) {
      setModalState({
        isOpen: true,
        status: 'error',
        message: lastError,
      });
      return;
    }
    
    setModalState({
      isOpen: true,
      status: 'loading',
      message: 'Processing payment...',
      modalOpen: true
    });

    try {
      const response = await payMembership(form);    
      
      if(response.status !== 200) {
        const error = await response.json().catch(() => ({}));
        console.error("Error during payment processing:",  error);
        
        if (error.messages && error.messages.length > 0) {
          const apiMessage = error.messages[0].message;
          setModalState({
            isOpen: true,
            status: 'error',
            message: 'Error:' + apiMessage,
            modalOpen: true
          });
        } else {
          setModalState({
            isOpen: true,
            status: 'error',
            message: 'Payment processing error. Please try again.',
            modalOpen: true
          });
        }
        return;
        
      } 

      // Refrescar el membership después del pago exitoso
      if (user?.email) {
        await refreshMembership(user.email);
      }

      setModalState({
        isOpen: true,
        status: 'success',
        message: '¡Payment made successfully! 🎉',
        modalOpen: true
      });

    } catch (error) {
      setModalState({
          isOpen: true,
          status: 'error',
          message: 'Payment processing error. Please try again.',
          modalOpen: true
        });
      console.error("Payment processing error. Please try again:", error);
    }
    
  };

  return (
    <div className="payment-form-container">
      <header className="payment-header">
        <img src={logo} alt="INVESCASA logo" className="invescasa-logo" />
        <span className="dashboard-text">Dashboards</span>
      </header>

      <h2 className="payment-title">Pay with card</h2>

      <form className="payment-form" onSubmit={handleSubmit}>
        <div className="input-group">
          <label className="input-label">Credit/debit card</label>
          <input
            type="email"
            name="email"
            className="input-field"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
          />
        </div>

        <div className="input-group input-icon-wrapper">
          <label className="input-label">Card number</label>
          <input
            type="text"
            name="cardNumber"
            className="input-field"
            placeholder="1234 1234 1234 1234"
            value={form.cardNumber}
            onChange={handleChange}
            maxLength={19}
          />
          <img src={cardPay} alt="Card icon" className="card-icon" />
        </div>

        <div className="row-small-inputs">
          <div className="input-group">
            <label className="input-label">Expire date</label>
            <input
              type="text"
              name="expireDate"
              className="input-field"
              placeholder="MM/YY"
              value={form.expireDate}
              onChange={handleExpireDateChange}
              maxLength={5}
            />
          </div>

          <div className="input-group">
            <label className="input-label">CVV</label>
            <input
              type="text"
              name="cvv"
              className="input-field"
              placeholder="CVV"
              value={form.cvv}
              onChange={handleChange}
              maxLength={4}
            />
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Name on card</label>
          <input
            type="text"
            name="cardName"
            className="input-field"
            placeholder="Card name"
            value={form.cardName}
            onChange={handleChange}
          />
        </div>

        <div className="input-group">
          <label className="input-label">Billing address</label>
          <input
            type="text"
            name="address"
            className="input-field"
            placeholder="Billing address"
            value={form.address}
            onChange={handleChange}
          />
        </div>

        <div className="input-group">
          <label className="input-label">City address</label>
          <input
            type="text"
            name="city"
            className="input-field"
            placeholder="City address"
            value={form.city}
            onChange={handleChange}
          />
        </div>

        <div className="input-group" ref={countryDropdownRef}>
          <label className="input-label">Country or region</label>
          <input
            type="text"
            name="countrySearch"
            className="input-field country-search-input"
            placeholder="Search country..."
            value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
            onFocus={() => setShowCountryDropdown(true)}
            autoComplete="off"
          />
          {showCountryDropdown && (
            <div className="country-dropdown">
              {filteredCountries.length > 0 ? (
                filteredCountries.map(([code, name]) => (
                  <div
                    key={code}
                    className="country-option"
                    onClick={() => handleCountrySelect(code, name)}
                  >
                    <span className="country-code">{code}</span>
                    <span className="country-name">{name}</span>
                  </div>
                ))
              ) : (
                <div className="country-option country-option-empty">
                  No countries found
                </div>
              )}
            </div>
          )}
        </div>

        <button type="submit" className="subscribe-btn">
          Subscribe
        </button>
      </form>

      {/* 🌀 Modal de carga */}
      <LoadingModal 
        isOpen={modalState.isOpen}
        status={modalState.status}
        message={modalState.message}
        onClose={() => {
          setModalState({ ...modalState, isOpen: false });
          // Solo redirigir al dashboard si el pago fue exitoso
          if (modalState.status === 'success') {
            navigate("/dashboard");
          }
        }}
      />

      <p className="subscription-note">
        By confirming your subscription, you allow INVESCASA to charge you for future payments in accordance with their terms. You can always cancel your subscription.
      </p>

      <p className="terms-text">
        By placing your order, you agree to our{' '}
        <a
          href="#!"
          className="terms-link"
          onClick={(e) => e.preventDefault()}
          role="button"
          tabIndex={0}
        >
          Terms of service
        </a>{' '}
        and{' '}
        <a
          href="#!"
          className="terms-link"
          onClick={(e) => e.preventDefault()}
          role="button"
          tabIndex={0}
        >
          privacy policy
        </a>.
      </p>

      <p className="powered-text">
        Powered by <b>Secure pay</b>
      </p>
    </div>
  );
};

export default PaymentForm;
